import { create } from 'zustand'
import type { User, LoginRequest, SignupRequest } from '@/types/auth.types'
import { authService } from './authService'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  // Actions
  login: (credentials: LoginRequest) => Promise<void>
  signup: (userData: SignupRequest) => Promise<void>
  logout: () => void
  loadUser: () => Promise<void>
  clearError: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  user: authService.getStoredUser(),
  isAuthenticated: authService.isAuthenticated(),
  isLoading: false,
  error: null,

  login: async (credentials: LoginRequest) => {
    set({ isLoading: true, error: null })
    try {
      await authService.login(credentials)
      // Fetch complete user data with roles/permissions from /auth/me
      const user = await authService.getCurrentUser()
      set({
        user,
        isAuthenticated: true,
        isLoading: false,
      })
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Login failed'
      set({
        error: message,
        isLoading: false,
        isAuthenticated: false,
        user: null,
      })
      throw error
    }
  },

  signup: async (userData: SignupRequest) => {
    set({ isLoading: true, error: null })
    try {
      await authService.signup(userData)
      // Fetch complete user data with roles/permissions from /auth/me
      const user = await authService.getCurrentUser()
      set({
        user,
        isAuthenticated: true,
        isLoading: false,
      })
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Signup failed'
      set({
        error: message,
        isLoading: false,
        isAuthenticated: false,
        user: null,
      })
      throw error
    }
  },

  logout: () => {
    authService.logout()
    set({
      user: null,
      isAuthenticated: false,
      error: null,
    })
  },

  loadUser: async () => {
    console.log('authStore.loadUser - Starting...')
    if (!authService.isAuthenticated()) {
      console.log('authStore.loadUser - Not authenticated, exiting')
      set({ isAuthenticated: false, user: null })
      return
    }

    console.log('authStore.loadUser - User is authenticated, fetching user data')
    set({ isLoading: true })
    try {
      console.log('authStore.loadUser - Calling authService.getCurrentUser()')
      const user = await authService.getCurrentUser()
      console.log('authStore.loadUser - Got user data:', user)
      set({
        user,
        isAuthenticated: true,
        isLoading: false,
      })
    } catch (error) {
      console.error('authStore.loadUser - Error:', error)
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      })
    }
  },

  clearError: () => set({ error: null }),
}))
